import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ScanRecentRow, type TaskStatus } from '../../lib/api.js';
import { asVerdict } from '../../lib/verdict.js';
import type { VerdictValue } from '@threatlens/design-system';
import { scanPermalink } from '../../lib/scan-link.js';

/**
 * Recent public analyses, as proof the platform is actually running rather than a claim that it is.
 *
 * Both sources are public and unmetered, so this panel costs an anonymous visitor nothing from their
 * daily lookup allowance. The heavier community endpoints are deliberately not used here: they
 * decode hundreds of full report bodies per call, which is the read pattern that has exhausted this
 * service's heap before.
 *
 * PRESENTATION NOTE. This was two lists of links with a verdict word floated right. It is now two
 * real tables with column headers, because the live corpus IS the argument for the product and a
 * bulleted list does not read as evidence. Columns are aligned and every figure inherits the
 * tabular numerals set on `body`, so the score column scans vertically instead of wobbling.
 */

/** Both feeds are public and unmetered, so this costs a visitor nothing from their lookup allowance. */
const REFRESH_MS = 20_000;

const VERDICT_TONE: Record<VerdictValue, string> = {
  malicious: 'text-verdict-malicious',
  suspicious: 'text-verdict-suspicious',
  clean: 'text-verdict-clean',
  unknown: 'text-text-muted',
  insufficient: 'text-text-muted',
};

function tone(v: string | undefined): string {
  return VERDICT_TONE[asVerdict(v)];
}

function Th({ children, right, full }: { children: React.ReactNode; right?: boolean; full?: boolean }) {
  return (
    <th
      scope="col"
      className={`whitespace-nowrap px-3 py-2 text-micro font-medium uppercase tracking-wide text-text-muted ${
        right ? 'text-right' : 'text-left'
      } ${full ? 'w-full' : ''}`}
    >
      {children}
    </th>
  );
}

/** One table shell so the two feeds cannot drift apart visually. */
function Panel({
  title,
  href,
  hrefLabel,
  children,
}: {
  title: string;
  href: string;
  hrefLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-sm border border-border-subtle">
      <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-3 py-2">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <Link to={href} className="shrink-0 text-micro font-medium text-accent-500 hover:underline">
          {hrefLabel}
        </Link>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export function RecentAnalyses() {
  const [files, setFiles] = useState<TaskStatus[]>([]);
  const [scans, setScans] = useState<ScanRecentRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  /**
   * Polled, because a panel headed "Analyzed just now" that was fetched once on mount is showing
   * whatever happened when the page loaded, which stops being "just now" the moment a visitor reads
   * anything else on the page.
   *
   * Polling pauses while the tab is hidden and refreshes immediately when it becomes visible again,
   * so a backgrounded tab does not sit there requesting a feed nobody is looking at, and a visitor
   * returning to the tab sees current data rather than whatever was current when they left. A failed
   * poll keeps the rows already on screen: stale-but-labelled beats emptying the panel.
   */
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      void api
        .firehose(24)
        .then((r) => {
          if (cancelled) return;
          setFiles((r.tasks ?? []).filter((t) => t.reportReady && t.contentHash).slice(0, 8));
          // Stamped from the feed that drives the file rows, so the time shown is when data actually
          // arrived rather than when a request was merely attempted.
          setUpdatedAt(new Date());
        })
        .catch(() => undefined);
      void api
        .scanRecent(12)
        .then((r) => {
          if (!cancelled) setScans((r.scans ?? []).slice(0, 8));
        })
        .catch(() => undefined);
    };

    load();
    const timer = setInterval(load, REFRESH_MS);
    document.addEventListener('visibilitychange', load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', load);
    };
  }, []);

  // Nothing to show is not an error worth a placeholder: the section simply does not render.
  if (files.length === 0 && scans.length === 0) return null;

  return (
    <section className="mt-12" aria-labelledby="recent">
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id="recent" className="flex flex-wrap items-baseline gap-x-3 text-section font-semibold text-text-primary">
          Live from the corpus
          {updatedAt && (
            <span className="font-mono text-micro font-normal text-text-muted">
              updated {updatedAt.toLocaleTimeString()}
            </span>
          )}
        </h2>
        <p className="text-micro text-text-muted">Public analyses only. Every row links to its full report.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {files.length > 0 && (
          <Panel title="Latest samples" href="/firehose" hrefLabel="Live feed">
            <table className="w-full border-collapse text-left">
              <thead className="border-b border-border-subtle">
                <tr>
                  <Th full>Sha256</Th>
                  <Th>Type</Th>
                  <Th>Verdict</Th>
                  <Th right>Score</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {files.map((t) => (
                  <tr key={t.taskId} className="transition-colors hover:bg-bg-overlay">
                    <td className="px-3 py-1.5">
                      <Link
                        to={`/report/${t.contentHash}`}
                        className="font-mono text-xs text-accent-500 hover:underline"
                        title={t.contentHash}
                      >
                        {t.contentHash?.slice(0, 20)}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-micro text-text-muted">{t.fileType ?? '-'}</td>
                    <td className={`px-3 py-1.5 text-micro font-semibold uppercase ${tone(t.staticVerdict?.verdict)}`}>
                      {t.staticVerdict?.verdict ?? 'analyzed'}
                    </td>
                    {/* An absent score renders as a dash, never as 0: zero is a real reading. */}
                    <td className="px-3 py-1.5 text-right font-mono text-micro text-text-secondary">
                      {typeof t.staticVerdict?.score === 'number' ? t.staticVerdict.score : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}

        {scans.length > 0 && (
          <Panel title="Latest URL scans" href="/scan" hrefLabel="Scan a URL">
            <table className="w-full border-collapse text-left">
              <thead className="border-b border-border-subtle">
                <tr>
                  {/* The host column absorbs the free width so the three fixed-width columns on the
                      right size to their content. Without it the host truncates to a stub while the
                      numeric columns sit in a sea of gap. */}
                  <Th full>Host</Th>
                  <Th>Verdict</Th>
                  <Th right>Malware</Th>
                  <Th right>Score</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {scans.map((s) => (
                  <tr key={s.uuid} className="transition-colors hover:bg-bg-overlay">
                    <td className="w-full max-w-0 px-3 py-1.5">
                      <Link
                        to={scanPermalink(s.uuid, s.hostname)}
                        className="block truncate text-xs text-accent-500 hover:underline"
                        title={s.url}
                      >
                        {s.hostname || s.url}
                      </Link>
                    </td>
                    <td className={`px-3 py-1.5 text-micro font-semibold uppercase ${tone(s.verdict)}`}>{s.verdict}</td>
                    {/* How many corpus samples talk to this host. A dash when none, because "0 malware"
                        reads as a cleanliness claim we have not made. */}
                    <td className="px-3 py-1.5 text-right font-mono text-micro text-text-secondary">
                      {s.correlatedSamples ? s.correlatedSamples : '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-micro text-text-secondary">
                      {typeof s.score === 'number' ? s.score : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
      </div>
    </section>
  );
}
